import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { FieldValues, useForm } from 'react-hook-form';

import { Icons, Modal, PageTitle, PercentsSpinner, Table, UnavailableFeatureMessage } from '@components/common';
import HistoryCheckBranch from '@components/routes/CmModule/HistoryCheck/HistoryCheckBranch';

import { useHistoryCheck } from '@hooks/historyCheck/useHistoryCheck';
import { useVendorDrivenFeatureAvailability } from '@hooks/utils/useVendorDrivenFeatureAvailability';

import { configurationManagement } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { isEmpty, isNotEmpty } from '@utils/common';
import { startOfDayInUTC } from '@utils/kpi-utils/date';

import { IBranchProgress } from '@interfaces/cmModule/cmModule';
import { IBranchTable } from '@interfaces/cmModule/configurationComparison';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch, ITableSort,
} from '@interfaces/components/table';

import { defaultMeta, EVendorsValues } from '@enums/common';
import { EValidationMode } from '@enums/validationMode';
import { EParametersFormValues } from './enums';

import {
	BRANCH_TABLE_SIZE,
	emptyTableText,
	emptyTableTextAwtgRan,
	exportActionsHistoryCheckBranches,
	exportBranchTooltip,
	historyCheckTitle,
	searchTooltipText,
} from './constants';

import HistoryCheckOptions from './HistoryCheckOptions';
import { getColumns } from './manageHistoryCheck';

import styles from './HistoryCheck.module.scss';

const HistoryCheck = () => {
	const { selectedVendor } = useAppSelector(configurationManagement);

	const {
		setValue,
		watch,
		control,
		register,
		resetField,
	} = useForm<FieldValues>({
		mode: EValidationMode.onSubmit,
		defaultValues: {
			[EParametersFormValues.vendor]: null,
			[EParametersFormValues.version]: null,
			[EParametersFormValues.configType]: null,
			[EParametersFormValues.technology]: null,
			[EParametersFormValues.nodeType]: null,
			[EParametersFormValues.from]: null,
			[EParametersFormValues.to]: null,
			[EParametersFormValues.siteIds]: [],
			[EParametersFormValues.nodes]: [],
			[EParametersFormValues.networkElements]: [],
		},
	});

	useEffect(() => {
		if (selectedVendor) {
			setValue(EParametersFormValues.vendor, selectedVendor);
		}
	}, [selectedVendor, setValue]);

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');
	const [selectedTableBranch, setSelectedTableBranch] = useState<IBranchTable>();

	const [isDirty, setIsDirty] = useState(false);
	const [isOpenOptions, setIsOpenOptions] = useState(true);

	const vendor = watch(EParametersFormValues.vendor)?.value;
	const technology = watch(EParametersFormValues.technology)?.value;
	const nodeType = watch(EParametersFormValues.nodeType)?.value;
	const dateFrom = watch(EParametersFormValues.from);
	const dateTo = watch(EParametersFormValues.to);
	const configType = watch(EParametersFormValues.configType)?.value;
	const version = watch(EParametersFormValues.version)?.value;
	const nodes = watch(EParametersFormValues.nodes);
	const siteIds = watch(EParametersFormValues.siteIds).map(({ value }: { value: string }) => value);
	const networkElements = watch(EParametersFormValues.networkElements);

	const nodesValues = useMemo(() => nodes.map((item: TSelectOption) => item?.value), [nodes]);
	const networkElementIds = useMemo(() =>
		networkElements.map((item: TSelectOption) => item?.value), [networkElements]);

	const isMavenirVendor = vendor === EVendorsValues.mavenir;
	const isAwtgRanVendor = vendor === EVendorsValues.oran;

	const disableDatepickerRange = isMavenirVendor
		? !(version && configType && nodes.length)
		: isAwtgRanVendor
			? !(configType && networkElements.length)
			: !(vendor && technology && nodeType && siteIds.length);

	const isDisabledSubmit = disableDatepickerRange || !(dateTo && dateFrom);

	useEffect(() => {
		resetField(EParametersFormValues.technology);
		resetField(EParametersFormValues.configType);
		setValue(EParametersFormValues.nodes, []);
		setValue(EParametersFormValues.networkElements, []);
	}, [vendor, resetField, setValue]);

	useEffect(() => {
		resetField(EParametersFormValues.nodeType);
	}, [technology, resetField]);

	useEffect(() => {
		resetField(EParametersFormValues.version);
	}, [nodes, resetField]);

	useEffect(() => {
		resetField(EParametersFormValues.configType);
	}, [version, resetField]);

	useEffect(() => {
		setValue(EParametersFormValues.from, null);
		setValue(EParametersFormValues.to, null);
		setValue(EParametersFormValues.siteIds, []);
		setValue(EParametersFormValues.networkElements, []);
	}, [nodeType, configType, setValue]);

	const toggleOptions = useCallback(() => setIsOpenOptions(!isOpenOptions), [isOpenOptions]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
	}, []);

	const openTableBranch = useCallback((data: IBranchTable) => setSelectedTableBranch(data), []);

	const closeTableBranch = useCallback(() => setSelectedTableBranch(undefined), []);

	const {
		technologiesOptions,
		nodesTypesOptions,
		datesOptions,
		configTypeOptions,
		nodesOptions,
		versionsOptions,
		branchData,
		generateProgressId,
		branchDataTotalCount,
		isLoadingBranchData,
		isLoadingOptions,
		progressData,
		dumpId,
		siteIdsOptions,
		networkElementsOptions,
		exportBranches,
		exportAwtgRanBranches,
	} = useHistoryCheck({
		vendor,
		technology,
		nodeType,
		page,
		pageSize,
		dateFrom,
		dateTo,
		siteIds,
		searchValue,
		sortFieldName,
		sortDirection,
		nodes: nodesValues,
		version,
		type: configType,
		isMavenirVendor,
		isAwtgRanVendor,
		networkElementIds,
	});

	const columns = useMemo(() => {
		return getColumns({ openTableBranch, isMavenirVendor, isAwtgRanVendor });
	}, [openTableBranch, isMavenirVendor, isAwtgRanVendor]);

	const onSubmit = useCallback(() => {
		generateProgressId();
		setPage(defaultMeta.page);
		setPageSize(defaultMeta.pageSize);
		setSearchValue('');
		setIsDirty(true);
		setIsOpenOptions(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [generateProgressId, dateFrom, dateTo]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);
	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: branchDataTotalCount,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [branchDataTotalCount, page, pageSize, pageHandler, pageSizeHandler]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'name',
			handler: searchHandler,
			tooltipText: searchTooltipText,
		},
	], [searchValue, searchHandler]);

	const handleExport = useCallback(() => {
		if (!dumpId) return;

		if (isAwtgRanVendor) {
			const dto = {
				dumpHistoryId: dumpId,
				filter: searchValue ? { searchValue } : {},
				isAll: true,
			};

			exportAwtgRanBranches(dto);
		} else {
			const dto = {
				dumpId,
				ids: [],
			};

			exportBranches(dto);
		}
	}, [exportBranches, exportAwtgRanBranches, dumpId, isAwtgRanVendor, searchValue]);

	const isFeatureAvailable = useVendorDrivenFeatureAvailability();

	return isFeatureAvailable ? (
		<div className={styles.historyCheckContainer}>
			{selectedTableBranch && (
				<Modal
					height={BRANCH_TABLE_SIZE}
					isOpen={!isEmpty(selectedTableBranch)}
					onClose={closeTableBranch}
					title={isAwtgRanVendor ? selectedTableBranch?.dataModelPath : selectedTableBranch?.distName}
					width={BRANCH_TABLE_SIZE}
				>
					<HistoryCheckBranch
						isAwtgRanVendor={isAwtgRanVendor}
						isMavenirVendor={isMavenirVendor}
						sameDay={(dateFrom === dateTo && startOfDayInUTC(new Date(dateFrom))) as number}
						selectedTableBranch={selectedTableBranch}
						vendor={vendor}
					/>
				</Modal>
			)}
			<div className={styles.pageTitleWrapper}>
				<PageTitle
					icon={Icons.history({ isTitle: true })}
					title={historyCheckTitle}
				/>
			</div>
			<div className={styles.historyCheckContent}>
				<HistoryCheckOptions
					configTypeOptions={configTypeOptions}
					control={control}
					datesOptions={datesOptions}
					disableDatepickerRange={disableDatepickerRange}
					handleSubmit={onSubmit}
					isAwtgRan={isAwtgRanVendor}
					isDisabledSubmit={isDisabledSubmit || isLoadingBranchData}
					isLoading={isLoadingOptions}
					isMavenir={isMavenirVendor}
					isOpenOptions={isOpenOptions}
					networkElementsOptions={networkElementsOptions}
					nodeTypesOptions={nodesTypesOptions}
					nodesOptions={nodesOptions}
					register={register}
					setValue={setValue}
					siteIdsOptions={siteIdsOptions}
					technologiesOptions={technologiesOptions}
					toggleOptions={toggleOptions}
					versionsOptions={versionsOptions}
					watch={watch}
				/>
				<div className={styles.historyCheckTableWrapper}>
					{!branchData?.length && !isDirty
						? <div className={styles.emptyMessage}>
							{isAwtgRanVendor ? emptyTableTextAwtgRan : emptyTableText}
						</div>
						: <Table
							columns={columns}
							customExportActions={exportActionsHistoryCheckBranches}
							data={branchData}
							exportTooltipText={exportBranchTooltip}
							handleCustomExport={handleExport}
							isExport
							isLoading={isLoadingBranchData}
							isWithoutCheckbox
							isWithoutDeleteAction
							isWithoutFilter
							loadingComponent={isNotEmpty<IBranchProgress>(progressData)
								? <PercentsSpinner
									percentsProgress={progressData?.percentsProgress}
									savedBranches={progressData?.savedBranches}
									totalBranchesToSave={progressData?.totalBranchesToSave}
								/>
								: undefined}
							pagination={pagination}
							search={search}
							searchThreshold={0}
							sort={sort}
						/>
					}
				</div>
			</div>
		</div>
	) : <UnavailableFeatureMessage vendorName={selectedVendor?.value} />;
};

export default memo(HistoryCheck);
